import { RestrictToVerticalAxis } from "@dnd-kit/abstract/modifiers";
import { DragDropProvider, KeyboardSensor, PointerSensor } from "@dnd-kit/react";
import { isSortable, useSortable } from "@dnd-kit/react/sortable";
import {
  DataGrid,
  DataGridCellSelection,
  type DataGridCellSelectionApi,
  type DataGridCellsChangeDetails,
  type DataGridFeatures,
  DataGridTableBodyRowCell,
  DataGridTableHeadRowCell,
  getPinningStyles,
} from "@repo/ui/components/reui/data-grid";
import { Button } from "@repo/ui/components/ui/button";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@repo/ui/components/ui/table";
import { cn } from "@repo/ui/lib/utils";
import type { Row, Table as TanstackTable } from "@tanstack/react-table";
import { GripVertical } from "lucide-react";
import {
  type ComponentProps,
  createContext,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  type RefObject,
  use,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import type { PresentedOrderSetParameterDto } from "@/lib/order-set-presentation";
import { trialParameterKey } from "./trial-values";

export interface TrialEditRequest {
  rowId: string;
  columnId: string;
  initialText?: string;
}

const TrialGridContext = createContext<{
  /** Parts read column structure only, never row data. */
  table: TanstackTable<DataGridFeatures, object>;
  parameters: readonly PresentedOrderSetParameterDto[];
  reorderable: boolean;
} | null>(null);

const TrialGridRowContext = createContext<{ handleRef: (node: HTMLElement | null) => void } | null>(null);

const REORDER_SENSORS = [PointerSensor, KeyboardSensor];

function useTrialGrid() {
  const context = use(TrialGridContext);
  if (!context) throw new Error("TrialGrid parts must render inside TrialGrid.Root");
  return context;
}

function TrialGridRoot<TData extends object>({
  table,
  parameters,
  label,
  describedBy,
  selection,
  onCellsChange,
  onCellEditRequest,
  onKeyDownCapture,
  onReorder,
  reference,
  children,
}: {
  table: TanstackTable<DataGridFeatures, TData>;
  parameters: readonly PresentedOrderSetParameterDto[];
  label: string;
  describedBy?: string;
  selection: RefObject<DataGridCellSelectionApi | null>;
  onCellsChange?: (details: DataGridCellsChangeDetails<TData>) => void;
  onCellEditRequest?: (request: TrialEditRequest) => void;
  onKeyDownCapture?: (event: KeyboardEvent<HTMLDivElement>) => void;
  /** Turns on drag-to-reorder: rows become sortable by their handle, and a drop reports the move. */
  onReorder?: (from: number, to: number) => void;
  /** Read as a reference rather than a record: a dashed, muted surface, so a plan never looks like work done. */
  reference?: boolean;
  children: ReactNode;
}) {
  const grid = (
    <TrialGridContext
      value={{
        table: table as unknown as TanstackTable<DataGridFeatures, object>,
        parameters,
        reorderable: !!onReorder,
      }}
    >
      <DataGrid
        table={table}
        recordCount={table.getRowModel().rows.length}
        tableLayout={{ cellSelection: true, cellEditMode: "click", dense: true, columnsPinnable: true }}
        onCellsChange={onCellsChange}
        onCellEditRequest={onCellEditRequest}
      >
        {/* Cell selection binds to the first viewport under this slot. Without it, it falls back to the parent
            and two grids in one section (plan above, performed below) share the first grid's viewport. */}
        <div data-slot="data-grid" className="relative">
          <div
            className={cn(
              !onReorder && "scroll-start-fade",
              reference
                ? "[--data-grid-surface:color-mix(in_oklab,var(--muted)_60%,var(--card))]"
                : "[--data-grid-surface:var(--card)]",
            )}
          >
            <div
              data-slot="data-grid-table-viewport"
              onKeyDownCapture={onKeyDownCapture}
              className={cn(
                "isolate w-full min-w-0 max-w-full overflow-x-auto bg-(--data-grid-surface) scrollbar-none rounded-md border [&_[data-slot=table-container]]:scrollbar-none",
                reference && "border-dashed text-muted-foreground",
              )}
            >
              <Table
                role="grid"
                tabIndex={0}
                data-slot="data-grid-table"
                aria-label={label}
                aria-describedby={describedBy}
                className="min-w-max [&_td]:px-3 [&_td]:py-1 [&_th]:px-3 [&:not(:has(tbody_tr))_thead_tr]:border-b-0"
              >
                {children}
              </Table>
            </div>
          </div>
          <DataGridCellSelection apiRef={selection} />
        </div>
      </DataGrid>
    </TrialGridContext>
  );
  if (!onReorder) return grid;
  return (
    <DragDropProvider
      sensors={REORDER_SENSORS}
      onDragEnd={(event) => {
        if (event.canceled) return;
        const { source } = event.operation;
        if (isSortable(source) && source.initialIndex !== source.index) onReorder(source.initialIndex, source.index);
      }}
    >
      {grid}
    </DragDropProvider>
  );
}

function TrialGridRow({ id, index, ...props }: ComponentProps<typeof TableRow> & { id: string; index: number }) {
  const { reorderable } = useTrialGrid();
  if (!reorderable) return <TableRow {...props} />;
  return <TrialGridSortableRow id={id} index={index} {...props} />;
}

function TrialGridSortableRow({
  id,
  index,
  className,
  ...props
}: ComponentProps<typeof TableRow> & { id: string; index: number }) {
  const { ref, handleRef, isDragging } = useSortable({ id, index, modifiers: [RestrictToVerticalAxis] });
  const handle = useRef<HTMLElement | null>(null);
  const thaw = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!isDragging) thaw.current?.();
  }, [isDragging]);
  return (
    <TrialGridRowContext
      value={{
        handleRef: (node) => {
          handle.current = node;
          handleRef(node);
        },
      }}
    >
      <TableRow
        ref={ref}
        className={cn(isDragging && "opacity-50", className)}
        onPointerDownCapture={(event) => {
          if (!handle.current?.contains(event.target as Node)) return;
          thaw.current?.();
          const row = event.currentTarget;
          const unfreeze = freezeForDrag(row);
          // Thaw only once the row is back in the table: the drop animates the lifted row after the drag has ended.
          thaw.current = () => {
            thaw.current = null;
            if (!row.matches(":popover-open")) return unfreeze();
            row.addEventListener("toggle", function landed(toggle) {
              if ((toggle as ToggleEvent).newState !== "closed") return;
              row.removeEventListener("toggle", landed);
              unfreeze();
            });
          };
          window.addEventListener("pointerup", () => thaw.current?.(), { once: true });
        }}
        {...props}
      />
    </TrialGridRowContext>
  );
}

// The drag lifts the row out of the scroller at the table's full width, where nothing clips it and the actions column
// stops sticking. Fixing both on press, before the lift, keeps the row on screen exactly as the grid showed it.
function freezeForDrag(row: HTMLTableRowElement) {
  const scroller = row.closest('[data-slot="table-container"]');
  if (!scroller) return () => {};
  const shown = scroller.getBoundingClientRect();
  const full = row.getBoundingClientRect();
  row.style.clipPath = `inset(0 ${Math.max(0, full.right - shown.right)}px 0 ${Math.max(0, shown.left - full.left)}px)`;
  const pinned = [...row.querySelectorAll<HTMLElement>("td[data-pinned]")].map((cell) => {
    const saved = {
      cell,
      position: cell.style.position,
      start: cell.style.insetInlineStart,
      end: cell.style.insetInlineEnd,
    };
    const stuck = cell.getBoundingClientRect().left;
    cell.style.position = "relative";
    cell.style.insetInlineStart = "auto";
    cell.style.insetInlineEnd = "auto";
    cell.style.left = `${stuck - cell.getBoundingClientRect().left}px`;
    return saved;
  });
  return () => {
    row.style.clipPath = "";
    for (const { cell, position, start, end } of pinned) {
      cell.style.position = position;
      cell.style.insetInlineStart = start;
      cell.style.insetInlineEnd = end;
      cell.style.left = "";
    }
  };
}

function TrialGridDragHead() {
  const { reorderable } = useTrialGrid();
  if (!reorderable) return null;
  return <TrialGridPinnedHead column={DRAG} label="Reorder" />;
}

function TrialGridDragHandle<TData extends object>({
  row,
  label,
}: {
  row: Row<DataGridFeatures, TData>;
  label: string;
}) {
  const sortable = use(TrialGridRowContext);
  if (!sortable) return null;
  return (
    <DataGridTableBodyRowCell cell={row.getAllCellsByColumnId()[DRAG]}>
      <button
        ref={sortable.handleRef}
        type="button"
        aria-label={label}
        className="flex cursor-grab items-center text-muted-foreground hover:text-foreground active:cursor-grabbing"
      >
        <GripVertical className="size-4" />
      </button>
    </DataGridTableBodyRowCell>
  );
}

function TrialGridHeader({ children }: { children: ReactNode }) {
  return (
    <TableHeader>
      <TableRow className="hover:bg-transparent">{children}</TableRow>
    </TableHeader>
  );
}

function TrialGridBody({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <TableBody data-slot="data-grid-table-body" className={className}>
      {children}
    </TableBody>
  );
}

function TrialGridHead({ className, ...props }: ComponentProps<typeof TableHead>) {
  return <TableHead className={cn("font-semibold", className)} {...props} />;
}

function TrialGridParameterHeads() {
  const { parameters } = useTrialGrid();
  return parameters.map((parameter) => (
    <TrialGridHead key={trialParameterKey(parameter)} title={parameter.sourceLabel}>
      {parameter.label}
    </TrialGridHead>
  ));
}

const DRAG = "drag";
const ACTIONS = "actions";

// A pinned cell is opaque so columns slide under it, which hides the row's translucent hover; repaint it premixed.
const PINNED_HOVER = "[tr:hover>&]:bg-[color-mix(in_oklab,var(--muted)_50%,var(--data-grid-surface))]";
// Hugs its content; the grid-wide cell padding would leave the column wide around a single icon.
const PINNED_PADDING = "px-1!";
// A shadow instead of a separator line marks columns scrolled underneath. Paint containment would clip it.
const START_EDGE = "pinned-start-fade [contain:none]! [&[data-pinned=start][data-last-col=start]]:shadow-none!";
const END_EDGE = "pinned-end-fade [contain:none]! [&[data-pinned=end][data-last-col=end]]:shadow-none!";

/** The first column of a reorderable trial grid: the drag handle, pinned to the start of the scroller. */
export const TRIAL_GRID_DRAG_COLUMN = {
  id: DRAG,
  header: "",
  size: 0,
  enableCellSelection: false,
  meta: {
    cellClassName: cn(PINNED_HOVER, START_EDGE, PINNED_PADDING),
    headerClassName: cn(START_EDGE, PINNED_PADDING),
  },
};

/** The last column of every trial grid: row actions, pinned to the end of the scroller, right-aligned, sized to its content. */
export const TRIAL_GRID_ACTIONS_COLUMN = {
  id: ACTIONS,
  header: "",
  size: 0,
  enableCellSelection: false,
  meta: {
    cellClassName: cn(PINNED_HOVER, END_EDGE, PINNED_PADDING),
    headerClassName: cn(END_EDGE, PINNED_PADDING),
  },
};

export const TRIAL_GRID_INITIAL_STATE = { columnPinning: { start: [DRAG], end: [ACTIONS] } };

function TrialGridPinnedHead({ column, label }: { column: string; label: string }) {
  const { table } = useTrialGrid();
  const header = table.getFlatHeaders().find((entry) => entry.column.id === column);
  if (!header) return null;
  return (
    <DataGridTableHeadRowCell header={header}>
      <span className="sr-only">{label}</span>
    </DataGridTableHeadRowCell>
  );
}

function TrialGridActionsHead({ label }: { label: string }) {
  return <TrialGridPinnedHead column={ACTIONS} label={label} />;
}

function TrialGridActionsCell<TData extends object>({
  row,
  children,
}: {
  row: Row<DataGridFeatures, TData>;
  children?: ReactNode;
}) {
  return (
    <DataGridTableBodyRowCell cell={row.getAllCellsByColumnId()[ACTIONS]}>
      <div className="flex items-center justify-end gap-1">{children}</div>
    </DataGridTableBodyRowCell>
  );
}

/** The actions column's cell for a row the table does not model (upcoming, retracted). */
function TrialGridActionsSpacer() {
  const { table } = useTrialGrid();
  const column = table.getColumn(ACTIONS);
  if (!column) return null;
  return (
    <td
      data-pinned="end"
      data-last-col="end"
      style={getPinningStyles(column)}
      className={cn("isolate bg-(--data-grid-surface)", PINNED_HOVER, END_EDGE, PINNED_PADDING)}
    />
  );
}

function TrialGridCell<TData extends object>({
  row,
  column,
  label,
  invalid,
  children,
}: {
  row: Row<DataGridFeatures, TData>;
  column: string;
  label: string;
  invalid?: boolean;
  children: ReactNode;
}) {
  return (
    <DataGridTableBodyRowCell
      cell={row.getAllCellsByColumnId()[column]}
      dndRef={(node: HTMLTableCellElement | null) => {
        node?.setAttribute("role", "gridcell");
        node?.setAttribute("aria-label", label);
        if (invalid) node?.setAttribute("aria-invalid", "true");
        else node?.removeAttribute("aria-invalid");
        node?.classList.toggle("text-destructive", !!invalid);
      }}
    >
      {children}
    </DataGridTableBodyRowCell>
  );
}

// The editor covers the whole cell while the display stays laid out, hidden, so the column never resizes.
function TrialGridOverlay({ editor, children }: { editor?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-h-6 items-center gap-1">
      {editor ? (
        <div aria-hidden className="invisible flex items-center gap-1">
          {children}
        </div>
      ) : (
        children
      )}
      {editor}
    </div>
  );
}

export interface TrialGridAddActionsHandle {
  focus: () => void;
}

// Focus never moves the page: the button is revealed only when it is off screen.
function TrialGridAddActions({
  ref,
  onAdd,
  addLabel = "Add trial",
  onDuplicate,
  children,
}: {
  ref?: Ref<TrialGridAddActionsHandle>;
  onAdd: () => void;
  addLabel?: string;
  /** Omitted when there is no previous trial to copy. */
  onDuplicate?: () => void;
  /** Further ways to add trials, after the two every grid has. */
  children?: ReactNode;
}) {
  const button = useRef<HTMLButtonElement>(null);
  useImperativeHandle(ref, () => ({
    focus: () => {
      button.current?.focus({ preventScroll: true });
      button.current?.scrollIntoView({ block: "nearest", behavior: "instant" });
    },
  }));
  return (
    <div className="flex gap-2">
      <Button ref={button} type="button" variant="outline" size="sm" onClick={onAdd}>
        {addLabel}
      </Button>
      <Button type="button" variant="outline" size="sm" disabled={!onDuplicate} onClick={onDuplicate}>
        Duplicate previous
      </Button>
      {children}
    </div>
  );
}

/** A grid's small uppercase heading with a one-line hint, as in Planned above Performed. */
function TrialGridSectionLabel({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="flex items-baseline gap-2 pt-1">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
      <span className="text-xs text-muted-foreground/70">{hint}</span>
    </div>
  );
}

export const TrialGrid = {
  SectionLabel: TrialGridSectionLabel,
  AddActions: TrialGridAddActions,
  Root: TrialGridRoot,
  Row: TrialGridRow,
  DragHead: TrialGridDragHead,
  DragHandle: TrialGridDragHandle,
  Header: TrialGridHeader,
  Body: TrialGridBody,
  Head: TrialGridHead,
  ParameterHeads: TrialGridParameterHeads,
  ActionsHead: TrialGridActionsHead,
  ActionsCell: TrialGridActionsCell,
  ActionsSpacer: TrialGridActionsSpacer,
  Cell: TrialGridCell,
  Overlay: TrialGridOverlay,
};
